// useClassificationMenu.js
// Shared menu controller for classification chips

import {useCallback, useRef, useState} from 'react';

export function useClassificationMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorPosition, setAnchorPosition] = useState(null);
    const [open, setOpen] = useState(false);
    const valueRef = useRef(null);
    const onApplyCallbackRef = useRef(null);

    /**
     * Open the menu.
     *
     * IMPORTANT: In ProseMirror, the anchor DOM can be replaced right after dispatching a transaction.
     * So we always snapshot a stable anchorPosition and prefer anchoring by position while the menu is open.
     */
    const openMenu = useCallback((anchor, initialValue = {}, onApplyCallback, event) => {
        // Prefer anchoring to the element rect so the menu aligns with the element (not the cursor).
        if (anchor && typeof anchor.getBoundingClientRect === 'function') {
            const rect = anchor.getBoundingClientRect();
            setAnchorPosition({
                top: Math.round(rect.top),
                left: Math.round(rect.left),
            });
        } else if (event && typeof event.clientX === 'number' && typeof event.clientY === 'number') {
            // Fallback: anchor to the click point when we don't have a DOM anchor.
            setAnchorPosition({
                top: Math.round(event.clientY),
                left: Math.round(event.clientX),
            });
        } else {
            setAnchorPosition({top: 0, left: 0});
        }

        setAnchorEl(anchor || null);
        valueRef.current = initialValue;
        onApplyCallbackRef.current = onApplyCallback;
        setOpen(true);
    }, []);

    const closeMenu = useCallback(() => {
        setAnchorEl(null);
        setAnchorPosition(null);
        setOpen(false);
        valueRef.current = null;
        onApplyCallbackRef.current = null;
    }, []);

    const handleApply = useCallback((result) => {
        valueRef.current = result;
        if (onApplyCallbackRef.current) {
            onApplyCallbackRef.current(result);
        }
    }, []);

    const getValue = useCallback(() => {
        return valueRef.current;
    }, []);

    return {
        anchorEl,
        anchorPosition,
        open,
        getValue,
        openMenu,
        closeMenu,
        handleApply,
    };
}
